import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ReviewItem, ReviewRoute } from "./reviewSeed";

interface Props {
  item: ReviewItem | undefined;
}

const ROUTE_LINK_KEY: Record<ReviewRoute, string> = {
  "/admin/promotions": "dashboard.reviewConsole.panel.openInAdminQueue",
  "/my-promotions": "dashboard.reviewConsole.panel.openInMyPromotions",
  "/documents": "dashboard.reviewConsole.panel.openInDocuments",
};

export function SelectedReviewPanel({ item }: Props): JSX.Element {
  const { t } = useTranslation();

  if (!item) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-white">
          {t("dashboard.reviewConsole.panel.heading")}
        </h3>
        <p className="mt-3 text-sm text-slate-500">
          {t("dashboard.reviewConsole.panel.empty")}
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {item.subject}
            {item.ownerName ? ` - ${item.ownerName}` : ""}
          </p>
          <h3 className="mt-1 text-base font-semibold text-white">
            {item.title}
          </h3>
        </div>
        <Link
          to={item.route}
          className="shrink-0 rounded-full bg-blue-600 px-4 py-1.5 text-center text-xs font-semibold text-white shadow-md shadow-blue-100 transition hover:bg-blue-700"
        >
          {t(ROUTE_LINK_KEY[item.route])}
        </Link>
      </div>

      <dl className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t("dashboard.reviewConsole.panel.stageLabel")}
          </dt>
          <dd className="mt-1 text-sm text-white">
            {t(`dashboard.reviewConsole.queue.stages.${item.stage}`)}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t("dashboard.reviewConsole.panel.nextAction")}
          </dt>
          <dd className="mt-1 text-sm text-white">
            {t(ROUTE_LINK_KEY[item.route])}
          </dd>
        </div>
      </dl>

      {item.summary && (
        <div className="mt-5 border-t border-slate-200 pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t("dashboard.reviewConsole.panel.summaryLabel")}
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {item.summary}
          </p>
        </div>
      )}
    </section>
  );
}
